import { createClient, type SupabaseClient } from '@supabase/supabase-js';

import type { Database } from '@fastcloud/shared';

const STORAGE_PREFIX = 'fastcloud.admin-web';

type BrowserEnvironment = {
  VITE_SUPABASE_ANON_KEY?: string;
  VITE_SUPABASE_URL?: string;
};

type BrowserStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

function environment(): BrowserEnvironment {
  return import.meta.env as BrowserEnvironment;
}

/**
 * Keeps the admin browser session entirely separate from the subscriber app's
 * Expo storage. The public anon key is safe to bundle because every useful
 * operation remains behind RLS and role/MFA checked database functions.
 */
function adminStorage(): BrowserStorage {
  return {
    getItem(key) {
      return window.localStorage.getItem(`${STORAGE_PREFIX}:${key}`);
    },
    setItem(key, value) {
      window.localStorage.setItem(`${STORAGE_PREFIX}:${key}`, value);
    },
    removeItem(key) {
      window.localStorage.removeItem(`${STORAGE_PREFIX}:${key}`);
    },
  };
}

function configuredValues(): { url: string; anonKey: string } {
  const { VITE_SUPABASE_URL: url, VITE_SUPABASE_ANON_KEY: anonKey } = environment();
  if (!url || !anonKey) {
    throw new Error(
      'Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. Copy apps/admin-web/.env.example and configure the staff website environment.',
    );
  }
  return { url, anonKey };
}

export function createAdminSupabaseClient(
  values: { url: string; anonKey: string } = configuredValues(),
): SupabaseClient<Database> {
  return createClient<Database>(values.url, values.anonKey, {
    auth: {
      storage: adminStorage(),
      storageKey: 'auth',
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
  });
}

/** The only Supabase client used by the separately deployed staff website. */
export const adminSupabase = createAdminSupabaseClient();

/** Alias used by staff-facing features; it is still the isolated admin client. */
export const staffSupabase = adminSupabase;

export const ADMIN_WEB_STORAGE_PREFIX = STORAGE_PREFIX;
